import { defineStore } from 'pinia'
import { ref } from 'vue'
import { showToast } from 'vant'
import { t } from '@/i18n'
import { apiCollect, apiUncollect, apiCollectList } from '@/apis/theater'
import type { IResCollectListItem } from '@/types/apis/theater'
import { useUserStore } from './user'
import { usePaginatedStore } from '@/composables/useInteractionStore'

export const useCollectStore = defineStore('collect', () => {
  const userStore = useUserStore()

  // 收藏状态：仅用 Set 维护 episodeId，O(1) 查询
  // Set 只由 toggleCollect 写入，不由 loadList 初始化；加载阶段依赖 feed API 的 IsCollected 字段
  const collectedEpisodeIds = ref(new Set<number>())

  // Profile 列表展示的分页数据
  const paginated = usePaginatedStore<IResCollectListItem>({
    storageKey: 'collected-scripts',
    fetchApi: apiCollectList,
    getItemId: item => item.Id,
  })

  const isCollected = (episodeId: number): boolean => collectedEpisodeIds.value.has(episodeId)

  /** 切换收藏状态：乐观更新 Set → 动作 API → 刷新分页 Map（Profile 数据源） */
  const toggleCollect = async (
    episodeId: number,
    scriptId: number,
    currentCollectedState?: boolean,
  ): Promise<boolean> => {
    if (!userStore.isLogin) {
      showToast(t('common.messages.pleaseLogin'))
      return false
    }

    const isCurrentlyCollected = currentCollectedState ?? isCollected(episodeId)

    // 乐观更新 Set（仅改 Set，不改 Map）
    if (isCurrentlyCollected) {
      collectedEpisodeIds.value.delete(episodeId)
    } else {
      collectedEpisodeIds.value.add(episodeId)
    }

    try {
      if (isCurrentlyCollected) {
        await apiUncollect({ episodeId, scriptId })
      } else {
        await apiCollect({ episodeId, scriptId })
      }
      // 操作成功后仅刷新分页 Map（Profile 数据源），不重建 Set
      // Set 由乐观更新保证正确性，不应被列表 API 返回数据覆盖
      await paginated.loadList(1)
      return true
    } catch {
      // 回滚 Set
      if (isCurrentlyCollected) {
        collectedEpisodeIds.value.add(episodeId)
      } else {
        collectedEpisodeIds.value.delete(episodeId)
      }
      showToast(t('common.errors.operationFailed'))
      return false
    }
  }

  /** 加载分页列表（仅 Profile 使用，不触碰 Set） */
  const loadList = async (pageNum?: number) => {
    await paginated.loadList(pageNum)
  }

  /** 清除所有数据 */
  const clear = () => {
    collectedEpisodeIds.value = new Set()
    paginated.clear()
  }

  return {
    collectedEpisodeIds,
    list: paginated.list,
    loading: paginated.loading,
    loaded: paginated.loaded,
    hasMore: paginated.hasMore,
    isCollected,
    toggleCollect,
    loadList,
    clear,
  }
})
